import { FIELD_HALF_EXTENT } from '../game/grid';

/** Where everything lives on the homestead. The field is centred on the origin. */
export const LAYOUT = {
  house: { x: 0, z: -16.5, width: 8, depth: 6 },
  windmill: { x: -16, z: -15 },
  pond: { x: -15.5, z: 5, r: 4 },
  market: { x: 14, z: 1 },
  board: { x: 14, z: -5.5 },
  beehive: { x: -10, z: -10 },
  compost: { x: 10, z: -10 },
  spawn: { x: 0, z: 10 },
  fence: { minX: -26, maxX: 24, minZ: -26, maxZ: 17, gate: 2.4 },
  pasture: { x: 40, z: -38, r: 14 },
} as const;

/** Walkable rectangle (just inside the fence). */
export const BOUNDS = {
  minX: LAYOUT.fence.minX + 0.9,
  maxX: LAYOUT.fence.maxX - 0.9,
  minZ: LAYOUT.fence.minZ + 0.9,
  maxZ: LAYOUT.fence.maxZ - 0.9,
};

export interface Circle {
  x: number;
  z: number;
  r: number;
}

/** Trees inside the fence (also used as obstacles). */
export const YARD_TREES: readonly Circle[] = [
  { x: -22, z: -21, r: 1.1 },
  { x: 20, z: -21, r: 1.1 },
  { x: -22, z: 13, r: 1.0 },
  { x: 20.5, z: 13, r: 1.0 },
  { x: 8.5, z: -21, r: 1.0 },
  { x: -8, z: -22, r: 1.0 },
  { x: -23, z: -4, r: 1.0 },
  { x: 21, z: 7, r: 0.9 },
];

/** Circles the farmer cannot walk through. */
export const STATIC_OBSTACLES: readonly Circle[] = [
  // The house is a rectangle; approximate it with overlapping circles.
  { x: LAYOUT.house.x - 2.4, z: LAYOUT.house.z, r: 3.4 },
  { x: LAYOUT.house.x + 2.4, z: LAYOUT.house.z, r: 3.4 },
  { x: LAYOUT.windmill.x, z: LAYOUT.windmill.z, r: 2.4 },
  { x: LAYOUT.pond.x, z: LAYOUT.pond.z, r: LAYOUT.pond.r + 0.3 },
  { x: LAYOUT.market.x + 0.3, z: LAYOUT.market.z, r: 1.9 },
  { x: LAYOUT.board.x + 0.2, z: LAYOUT.board.z, r: 1.0 },
  ...YARD_TREES.map((t) => ({ ...t, r: 0.7 })),
];

/** Stepping-stone paths as polylines of (x, z) points. */
export const PATHS: ReadonlyArray<ReadonlyArray<readonly [number, number]>> = [
  [
    [0, -13.2],
    [0, -FIELD_HALF_EXTENT - 0.8],
  ],
  [
    [FIELD_HALF_EXTENT + 0.8, 0.5],
    [11.8, 1],
  ],
  [
    [11.8, 1],
    [12.3, -5.5],
  ],
  [
    [0, FIELD_HALF_EXTENT + 0.8],
    [0, LAYOUT.fence.maxZ + 1],
  ],
  [
    [-FIELD_HALF_EXTENT - 0.8, 2],
    [-10.8, 4],
  ],
];

function distanceToSegment(px: number, pz: number, ax: number, az: number, bx: number, bz: number) {
  const dx = bx - ax;
  const dz = bz - az;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / (dx * dx + dz * dz || 1)));
  return Math.hypot(px - (ax + dx * t), pz - (az + dz * t));
}

export function distanceToPaths(x: number, z: number): number {
  let best = Infinity;
  for (const path of PATHS) {
    for (let i = 0; i < path.length - 1; i++) {
      const a = path[i]!;
      const b = path[i + 1]!;
      best = Math.min(best, distanceToSegment(x, z, a[0], a[1], b[0], b[1]));
    }
  }
  return best;
}

/** True when decorative scatter (grass, wildflowers) may be placed at (x, z). */
export function isOpenGround(x: number, z: number, margin = 0.4): boolean {
  const field = FIELD_HALF_EXTENT + 0.5 + margin;
  if (Math.abs(x) < field && Math.abs(z) < field) return false;
  const h = LAYOUT.house;
  if (
    Math.abs(x - h.x) < h.width / 2 + 1 + margin &&
    Math.abs(z - h.z) < h.depth / 2 + 1 + margin
  ) {
    return false;
  }
  for (const o of STATIC_OBSTACLES) if (Math.hypot(x - o.x, z - o.z) < o.r + margin) return false;
  return distanceToPaths(x, z) > 0.75 + margin;
}
